import { defineStore } from "pinia";
import {
  Q_USERS_PORTAL,
  Q_USERS_CHECKOUT,
  Q_USER_CHECKOUT_LINK,
  Q_USER_AFFILIATE,
  Q_SUBSCRIPTION_DETAILS,
  Q_USER_INVOICES,
  Q_USER_UPCOMING_INVOICE,
  Q_USER_PAYMENT_METHODS,
  M_CREATE_SETUP_INTENT,
  M_CANCEL_SUBSCRIPTION,
  M_PAUSE_SUBSCRIPTION,
  M_RESUME_SUBSCRIPTION,
  M_SET_DEFAULT_PAYMENT_METHOD,
  M_DELETE_PAYMENT_METHOD,
  M_SWITCH_PLAN,
  M_APPLY_RETENTION_OFFER,
  M_CREATE_TRIAL_SUBSCRIPTION,
  M_CREATE_WEEKLY_SUBSCRIPTION,
  Q_VALIDATE_COUPON,
  M_SUBMIT_CANCELLATION_AUDIT,
  M_CHANGE_PLAN_TIER,
} from "../services/graphql";
import { graphql_url } from "../constants";
import { PlanTier } from "../constants/plan-tiers";
import { gql } from "graphql-request";
import { request, GraphQLClient } from "graphql-request";
import { loggerFactory } from "../logger";
import { setUser, captureError, captureProblem, track } from "src/rum";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { getClient } from "../supabase";
import day from "../utils/day";

const apiURL = graphql_url;
const logger = loggerFactory("SessionStore");

const retrieveMessageByErrorCode = (code, namespace = "") => {
  switch (code) {
    case "user_already_exists":
    case "auth/email-already-in-use":
      return "Looks like you might want to try logging in first.";
    case "auth/user-disabled":
      return "Your account has been disabled, please reach out to get further support.";
    case "auth/invalid-password":
    case "auth/wrong-password":
    case "invalid_credentials":
    case 400:
      return "Invalid email or password provided, please correct and try again.";
    case "auth/too-many-requests":
      return "Access to this account has been temporarily disabled due to many failed login attempts. You can immediately restore it by resetting your password or you can try again later.";
    case "auth/weak-password":
    case "weak_password":
      return "The password is known to be weak and easy to guess. Please input complex password with minimum 6 characters.";
    case "unexpected_failure":
    default:
      return "We were unable to process your request at the time.";
  }
};

export const useUserStore = defineStore(
  "session",
  {
    state: () => ({
      userId: null,
      profileId: null,
      sessionLoading: true,
      loadingUser: false,
      loading: false,
      fingerprint: null,
      subscribed: false,
      pastDue: false,
      pausedUntil: null,
      planTier: null,
      features: [],
      dailyTokenLimit: null,
      alertSoundEnabled: false,
    }),
    getters: {
      isSubscribed: (state) => state.subscribed,
      isPaused: (state) => !!state.pausedUntil && day(state.pausedUntil).isAfter(day()),
      canUpgrade: (state) => state.planTier === PlanTier.STARTER,
      canDowngrade: (state) => state.planTier === PlanTier.SKILLED,
      hasFeature: (state) => (feature) => state.features.includes(feature),
    },
    actions: {
      authState(callback) {
        logger.debug("Initializing auth state listener...");
        const auth = getClient().auth;
        return auth.onAuthStateChange((event, session) => {
          logger.debug("onAuthStateChange", event, session);
          this.sessionLoading = false;

          if (event === "SIGNED_OUT") {
            this.userId = null;
            this.profileId = null; // Reset profileId when user signs out
          }

          setTimeout(async () => {
            if (event === "SIGNED_OUT") {
              setUser(null);
            }

            if (session?.user) {
              this.userId = session.user.id;
              const phoneTrait = session.user.user_metadata?.phone
                ? { phone: session.user.user_metadata?.phone }
                : {};
              
              const providers = session.user.app_metadata?.providers || [];
              const discord_connected = providers.includes('discord');

              setUser(session.user.id, {
                email: session.user.email,
                name: session.user.user_metadata?.full_name,
                first_name: session.user.user_metadata?.first_name,
                last_name: session.user.user_metadata?.last_name,
                experience: session.user.user_metadata?.experience,
                discord_connected,
                ...phoneTrait,
              });
            }

            // Sync email column in users table after email-change confirmation
            if (event === "USER_UPDATED" && session?.user?.email) {
              try {
                await getClient()
                  .from("users")
                  .update({ email: session.user.email })
                  .or(`id.eq.${session.user.id},user_id.eq.${session.user.id}`);
                logger.debug("Synced email to users table after confirmation");
              } catch (e) {
                logger.error("Failed to sync email to users table", e);
              }
            }

            try {
              callback?.(event, session);
            } catch (e) {}
          }, 0);
        });
      },
      async currentUser() {
        const auth = getClient().auth;
        // https://supabase.com/docs/reference/javascript/auth-getsession
        // https://supabase.com/docs/reference/javascript/auth-getuser
        const {
          data: { user },
        } = await auth.getUser();

        if (user) {
          this.userId = user.id;
          // Identity is set by the auth state change listener (SIGNED_IN event)
          // so we don't need to call setUser again here
        }

        return user;
      },
      async sessionToken() {
        const { data, error } = await getClient().auth.getSession();
        if (error) throw error;

        if (data.session) {
          return data.session.access_token;
        }
      },
      async session() {
        const client = getClient();
        const auth = client.auth;
        const { data, error } = await auth.getSession();
        if (data?.session) {
          logger.debug("Received users session", data.session);
          const user = data.session.user;
          const token = data.session.access_token;
          let fName = data.session.user.user_metadata?.first_name;
          let lName = data.session.user.user_metadata?.last_name;

          const userResult = await client
            .from("users")
            .select("*")
            .or(`id.eq.${user.id},user_id.eq.${user.id}`)
            .limit(1)
            .single();
          if (userResult.error) {
            logger.debug("Error loading user", userResult.error);
            captureError(userResult.error);
            throw new Error("Unable to load your session.");
          }

          logger.debug("Retrieved profile", userResult);
          this.profileId = userResult.data?.id;
          fName = fName ?? userResult.data?.first_name;
          lName = lName ?? userResult.data?.last_name;
          // Enrich identity with profile data (may have fuller name than auth metadata)
          const providers = user.app_metadata?.providers || [];
          const discord_connected = providers.includes('discord');

          setUser(user.id, {
            email: data.session.user.email,
            first_name: fName,
            last_name: lName,
            name: `${fName} ${lName}`,
            experience: userResult.data?.experience,
            discord_connected,
          });
          // user is subscribed if they have a subscription id && not cancelled
          this.subscribed =
            !!userResult.data?.subscription &&
            (!userResult.data?.subscription_cancelled ||
              day(userResult.data?.subscription_cancelled).isAfter(day()));
          this.pastDue = userResult.data?.past_due || false;
          this.pausedUntil = userResult.data?.paused_until || null;
          await this.submitAuditAccess();

          // Eagerly populate planTier / features so plan-aware UI (e.g. upgrade
          // CTAs in the daily-limit dialog) works without visiting BillingPage.
          // Fire-and-forget — session() should not block on billing data.
          if (this.subscribed) {
            this.subscriptionDetails().catch((err) =>
              logger.warn('[session] background subscriptionDetails failed', err)
            );
          }

          return {
            id: user.id,
            subscribed: this.subscribed,
            pastDue: this.pastDue,
            paused: this.isPaused,
          };
        } else {
          return Promise.resolve(null);
        }
      },
      async billingPortal() {
        const auth = getClient().auth;
        const { data, error } = await auth.getSession();
        if (data.session) {
          const access_token = data.session.access_token;
          const graphQLClient = new GraphQLClient(apiURL, {
            credentials: `include`,
            mode: `cors`,
            headers: {
              authorization: `Bearer ${access_token}`,
            },
          });
          return graphQLClient.request(Q_USERS_PORTAL, {}).then((result) => {
            return result?.usersPortal;
          });
        } else {
          return Promise.resolve(null);
        }
      },
      async _authedClient() {
        const { data } = await getClient().auth.getSession();
        if (!data.session) return null;
        return new GraphQLClient(apiURL, {
          credentials: "include",
          mode: "cors",
          headers: { authorization: `Bearer ${data.session.access_token}` },
        });
      },
      async subscriptionDetails() {
        const client = await this._authedClient();
        if (!client) return null;
        return client.request(Q_SUBSCRIPTION_DETAILS, {}).then((result) => {
          if (result?.subscriptionDetails?.errors) {
            throw new Error(result.subscriptionDetails.errors[0]?.message ?? "Unable to load subscription details.");
          }
          const details = result?.subscriptionDetails;
          // Populate tier state so components can react without re-fetching
          if (details?.planTier) {
            this.planTier = details.planTier;
            this.features = details.features || [];
            this.dailyTokenLimit = details.dailyTokenLimit ?? null;
          }
          return details;
        });
      },
      async userInvoices() {
        const client = await this._authedClient();
        if (!client) return null;
        return client.request(Q_USER_INVOICES, {}).then((result) => {
          if (result?.userInvoices?.errors) {
            throw new Error(result.userInvoices.errors[0]?.message ?? "Unable to load invoices.");
          }
          return result?.userInvoices;
        });
      },
      async userUpcomingInvoice() {
        const client = await this._authedClient();
        if (!client) return null;
        return client.request(Q_USER_UPCOMING_INVOICE, {}).then((result) => {
          if (result?.userUpcomingInvoice?.errors) {
            throw new Error(result.userUpcomingInvoice.errors[0]?.message ?? "Unable to load upcoming invoice.");
          }
          return result?.userUpcomingInvoice;
        });
      },
      async userPaymentMethods() {
        const client = await this._authedClient();
        if (!client) return null;
        return client.request(Q_USER_PAYMENT_METHODS, {}).then((result) => {
          if (result?.userPaymentMethods?.errors) {
            throw new Error(result.userPaymentMethods.errors[0]?.message ?? "Unable to load payment methods.");
          }
          return result?.userPaymentMethods;
        });
      },
      async createSetupIntent() {
        const client = await this._authedClient();
        if (!client) return null;
        return client.request(M_CREATE_SETUP_INTENT, {}).then((result) => {
          if (result?.createSetupIntent?.errors) {
            const msg = result.createSetupIntent.errors[0]?.message ?? "Unable to create setup intent.";
            logger.error("createSetupIntent GraphQL failure", { userId: this.userId, error: msg });
            captureProblem(`Subscribe failure - createSetupIntent: ${msg}`, { userId: this.userId, error: msg });
            throw new Error(msg);
          }
          return result?.createSetupIntent;
        });
      },
      async cancelSubscription(reason, followUpAnswer, feedback) {
        const client = await this._authedClient();
        if (!client) return null;
        return client.request(M_CANCEL_SUBSCRIPTION, { reason, followUpAnswer, feedback }).then((result) => {
          if (result?.cancelSubscription?.errors) {
            throw new Error(result.cancelSubscription.errors[0]?.message ?? "Unable to cancel subscription.");
          }
          this.subscribed = false;
          return result?.cancelSubscription;
        });
      },
      async pauseSubscription(months) {
        const client = await this._authedClient();
        if (!client) return null;
        return client.request(M_PAUSE_SUBSCRIPTION, { months }).then((result) => {
          if (result?.pauseSubscription?.errors) {
            throw new Error(result.pauseSubscription.errors[0]?.message ?? "Unable to pause subscription.");
          }
          return result?.pauseSubscription;
        });
      },
      async resumeSubscription() {
        const client = await this._authedClient();
        if (!client) return null;
        return client.request(M_RESUME_SUBSCRIPTION, {}).then((result) => {
          if (result?.resumeSubscription?.errors) {
            throw new Error(result.resumeSubscription.errors[0]?.message ?? "Unable to resume subscription.");
          }
          // Clear pause state locally so routing/banner updates immediately
          this.pausedUntil = null;
          return result?.resumeSubscription;
        });
      },
      async applyRetentionOffer() {
        const client = await this._authedClient();
        if (!client) return null;
        return client.request(M_APPLY_RETENTION_OFFER, {}).then((result) => {
          if (result?.applyRetentionOffer?.errors) {
            throw new Error(result.applyRetentionOffer.errors[0]?.message ?? "Unable to apply retention offer.");
          }
          return result?.applyRetentionOffer;
        });
      },
      async switchSubscriptionPlan(annual, tier = null) {
        const client = await this._authedClient();
        if (!client) return null;
        return client.request(M_SWITCH_PLAN, { annual, tier }).then((result) => {
          if (result?.switchSubscriptionPlan?.errors) {
            throw new Error(result.switchSubscriptionPlan.errors[0]?.message ?? "Unable to switch subscription plan.");
          }
          return result?.switchSubscriptionPlan;
        });
      },
      async changePlanTier(tier, annual) {
        const client = await this._authedClient();
        if (!client) return null;
        return client.request(M_CHANGE_PLAN_TIER, { tier, annual }).then((result) => {
          if (result?.changePlanTier?.errors) {
            throw new Error(result.changePlanTier.errors[0]?.message ?? "Unable to change plan tier.");
          }
          // Update local state with new tier details
          const data = result?.changePlanTier;
          if (data?.success) {
            this.planTier = data.planTier;
            this.features = data.features ?? [];
            this.dailyTokenLimit = data.dailyTokenLimit;
          }
          return data;
        });
      },
      async setDefaultPaymentMethod(paymentMethodId) {
        const client = await this._authedClient();
        if (!client) return null;
        return client.request(M_SET_DEFAULT_PAYMENT_METHOD, { paymentMethodId }).then((result) => {
          if (result?.setDefaultPaymentMethod?.errors) {
            throw new Error(result.setDefaultPaymentMethod.errors[0]?.message ?? "Unable to set default payment method.");
          }
          return result?.setDefaultPaymentMethod;
        });
      },
      async deletePaymentMethod(paymentMethodId) {
        const client = await this._authedClient();
        if (!client) return null;
        return client.request(M_DELETE_PAYMENT_METHOD, { paymentMethodId }).then((result) => {
          if (result?.deletePaymentMethod?.errors) {
            throw new Error(result.deletePaymentMethod.errors[0]?.message ?? "Unable to delete payment method.");
          }
          return result?.deletePaymentMethod;
        });
      },
      async createTrialSubscription(paymentMethodId, annual = true, referralCode = null, couponCode = null, checkoutTosAcceptedAt = null, tier = null) {
        const client = await this._authedClient();
        if (!client) return null;
        const normalizedReferral = referralCode && typeof referralCode === "string" && referralCode.trim() ? referralCode.trim() : null;
        return client.request(M_CREATE_TRIAL_SUBSCRIPTION, { paymentMethodId, annual, referralCode: normalizedReferral, couponCode, checkoutTosAcceptedAt, tier }).then((result) => {
          if (result?.createTrialSubscription?.errors) {
            const msg = result.createTrialSubscription.errors[0]?.message ?? "Unable to start trial subscription.";
            logger.error("createTrialSubscription GraphQL failure", { userId: this.userId, annual, tier, referralCode: normalizedReferral, couponCode, error: msg });
            captureProblem(`Subscribe failure - createTrialSubscription: ${msg}`, {
              userId: this.userId,
              planType: annual ? "annual" : "monthly",
              annual,
              tier,
              referralCode: normalizedReferral,
              couponCode,
              error: msg,
            });
            throw new Error(msg);
          }
          this.subscribed = true;
          return result?.createTrialSubscription;
        });
      },
      async createWeeklySubscription(paymentMethodId, referralCode = null, checkoutTosAcceptedAt = null) {
        const client = await this._authedClient();
        if (!client) return null;
        const normalizedReferral = referralCode && typeof referralCode === "string" && referralCode.trim() ? referralCode.trim() : null;
        return client.request(M_CREATE_WEEKLY_SUBSCRIPTION, { paymentMethodId, referralCode: normalizedReferral, checkoutTosAcceptedAt }).then((result) => {
          if (result?.createWeeklySubscription?.errors) {
            const msg = result.createWeeklySubscription.errors[0]?.message ?? "Unable to start weekly subscription.";
            logger.error("createWeeklySubscription GraphQL failure", { userId: this.userId, referralCode: normalizedReferral, error: msg });
            captureProblem(`Subscribe failure - createWeeklySubscription: ${msg}`, {
              userId: this.userId,
              planType: "weekly",
              referralCode: normalizedReferral,
              error: msg,
            });
            throw new Error(msg);
          }
          this.subscribed = true;
          return result?.createWeeklySubscription;
        });
      },
      async validateCoupon(code) {
        const client = await this._authedClient();
        if (!client) return null;
        return client.request(Q_VALIDATE_COUPON, { code }).then((result) => {
          if (result?.validateCoupon?.errors) {
            throw new Error(result.validateCoupon.errors[0]?.message ?? "Unable to validate promo code.");
          }
          return result?.validateCoupon;
        });
      },
      async submitCancellationAudit({ action, metadata }) {
        const client = await this._authedClient();
        if (!client) return null;
        return client.request(M_SUBMIT_CANCELLATION_AUDIT, {
          action,
          ipAddress: metadata?.ip_address ?? null,
          fingerprint: metadata?.fingerprint ?? null,
          userAgent: metadata?.user_agent ?? null,
          metadata: JSON.stringify(metadata),
        }).then((result) => {
          if (result?.submitCancellationAudit?.errors) {
            throw new Error(result.submitCancellationAudit.errors[0]?.message ?? "Unable to submit cancellation audit.");
          }
          return result?.submitCancellationAudit;
        });
      },
      async signup(username, password, phone, firstName, lastName, experience, tosAcceptedAt) {
        logger.debug(`Additional attributes ${phone} ${firstName} ${lastName}`);
        const client = getClient();
        const auth = client.auth;
        const self = this;
        const signupRequest = {
          email: username,
          password: password,
          options: {
            data: {
              first_name: firstName,
              last_name: lastName,
              phone: phone,
              experience: experience,
            },
          },
        };

        // --- CRITICAL PATH: auth signup + user row (must never fail due to evidence capture) ---
        return auth
          .signUp(signupRequest)
          .then(async (response) => {
            if (response.error) throw response.error;
            const credential = response.data;
            logger.debug("signUp", credential);
            const user = credential.user;
            self.userId = user.id;

            // create metadata row — evidence fields added best-effort below
            const now = new Date().toISOString();
            const insertPayload = {
              id: user.id,
              user_id: user.id,
              email: username,
              phone: phone,
              first_name: firstName,
              last_name: lastName,
              logins: 1,
              last_login: now,
              created: now,
            };
            if (tosAcceptedAt) insertPayload.tos_accepted_at = tosAcceptedAt;

            // Best-effort: try to capture IP and fingerprint before inserting,
            // but proceed with insert regardless of outcome
            try {
              const [ipResult, fpResult] = await Promise.allSettled([
                fetch("https://api.ipify.org?format=json").then(r => r.ok ? r.json() : null),
                (async () => {
                  const cached = self.fingerprint;
                  if (cached) return cached;
                  const inst = await FingerprintJS.load({ monitoring: false });
                  const fp = await inst.get();
                  return fp?.visitorId || null;
                })(),
              ]);
              const ip = ipResult.status === "fulfilled" ? ipResult.value?.ip : null;
              const fp = fpResult.status === "fulfilled" ? fpResult.value : null;
              if (ip) insertPayload.signup_ip = ip;
              if (fp) {
                insertPayload.signup_fingerprint = fp;
                self.fingerprint = fp;
              }
            } catch (e) {
              // Evidence capture failed entirely — that's fine, proceed with signup
              logger.error("Non-critical: evidence capture failed", e);
            }

            const insertResult = await client
              .from("users")
              .insert(insertPayload)
              .select("id,email");

            if (insertResult?.error) {
              logger.error("signUp users table insert error", insertResult.error);
              captureProblem(
                `Signup users table insert failure - ${insertResult.error.code || "db_error"}: ${insertResult.error.message}`,
                {
                  userId: user?.id,
                  email: username,
                  code: insertResult.error.code,
                  details: insertResult.error.details,
                  hint: insertResult.error.hint,
                }
              );
              track("signup_db_insert_error", {
                userId: user?.id,
                email: username,
                code: insertResult.error.code,
                error: insertResult.error.message,
              });
              throw insertResult.error;
            }

            return insertResult?.data?.[0] || user;
          })
          .catch((error) => {
            logger.error("signUp", error);
            // track error
            captureProblem(
              `Signup failure - ${error?.name || error?.code || "Error"}: ${error.message}`,
              { email: username, ...error, json: error?.toJSON?.() }
            );
            track("signup_failed", {
              email: username,
              code: error?.code || error?.status,
              error: error?.message,
            });
            let errorMessage = retrieveMessageByErrorCode(
              error.status || error.code,
              error.name
            );
            if (error.message) errorMessage += ` ${error.message}`;

            throw new Error(errorMessage);
          });
      },
      async signIn(username, password) {
        const auth = getClient().auth;

        // login to supabase
        const singInResult = await auth.signInWithPassword({
          email: username,
          password: password,
        });
        if (singInResult.error) {
          logger.error(
            "Encountered error processing login",
            singInResult.error
          );
          logger.error("signInWithPassword", singInResult.error?.toJSON?.());
          captureProblem(
            `Login failure - ${singInResult?.error?.name}: ${singInResult.error.message}`,
            {
              email: username,
              ...singInResult.error,
              json: singInResult.error.toJSON?.(),
            }
          );
          let errorMessage = retrieveMessageByErrorCode(
            singInResult.error.status,
            singInResult.error.name
          );
          if (singInResult.error.message)
            errorMessage += ` ${singInResult.error.message}`;

          throw new Error(errorMessage);
        }

        const credential = singInResult.data;
        logger.debug("signInWithPassword", credential);

        // Signed in
        return credential.user;
      },
      async signInWithEmail(username) {
        const auth = getClient().auth;

        // login to supabase
        const sinInResult = await auth.signInWithOtp({ email: username });
        if (sinInResult.error) {
          logger.error(
            "Encountered error processing magic link login",
            sinInResult.error
          );
          logger.error("signInWithEmail", sinInResult.error?.toJSON?.());
          captureProblem(
            `MagicLink Login failure - ${sinInResult?.error?.name}: ${sinInResult.error.message}`,
            {
              email: username,
              ...sinInResult.error,
              json: sinInResult.error.toJSON?.(),
            }
          );
          let errorMessage = retrieveMessageByErrorCode(
            sinInResult.error.status,
            sinInResult.error.name
          );
          if (sinInResult.error.message)
            errorMessage += ` ${sinInResult.error.message}`;

          throw new Error(errorMessage);
        }

        return true;
      },
      async passwordReset(email) {
        const auth = getClient().auth;
        return auth
          .resetPasswordForEmail(email, {
            redirectTo: `${window.location.origin}/auth/confirm`,
          })
          .then((response) => {
            if (response.error) throw response.error;
            logger.debug("resetPasswordForEmail", response.data);
          })
          .catch((error) => {
            logger.error("resetPasswordForEmail", JSON.stringify(error));
            const errorMessage = retrieveMessageByErrorCode(
              error.code,
              error.name
            );
            throw new Error(errorMessage);
          });
      },
      async signUserOut() {
        const auth = getClient().auth;
        return auth
          .signOut()
          .then(() => {
            logger.debug("signOut");
            this.userId = null;
            this.profileId = null; // Reset profileId when user signs out
            setUser(null);
            return true;
          })
          .catch((error) => {
            logger.error("signOut", error);
            const errorMessage = retrieveMessageByErrorCode(
              error.code,
              error.name
            );
            throw new Error(errorMessage);
          });
      },
      async changePassword(code, password) {
        const auth = getClient().auth;
        return auth
          .updateUser({ password: password })
          .then(() => {
            logger.debug("updateUser");
          })
          .catch((error) => {
            logger.error("updateUser", error);
            const errorMessage = retrieveMessageByErrorCode(
              error.code,
              error.name
            );
            throw new Error(errorMessage);
          });
      },
      async updateProfile(firstName, lastName, phone) {
        const client = getClient();
        const auth = client.auth;
        const { data: { user } } = await auth.getUser();
        if (!user) throw new Error("Not authenticated.");

        // Update auth metadata
        const { error: authError } = await auth.updateUser({
          data: { first_name: firstName, last_name: lastName, phone },
        });
        if (authError) {
          logger.error("updateProfile auth metadata", authError);
          throw new Error(authError.message ?? "Unable to update profile.");
        }

        // Update users table
        const { error: dbError } = await client
          .from("users")
          .update({ first_name: firstName, last_name: lastName, phone })
          .or(`id.eq.${user.id},user_id.eq.${user.id}`);
        if (dbError) {
          logger.error("updateProfile users table", dbError);
          throw new Error(dbError.message ?? "Unable to save profile.");
        }

        logger.debug("updateProfile success");
      },
      async updateEmail(newEmail) {
        const auth = getClient().auth;
        const { error } = await auth.updateUser({ email: newEmail });
        if (error) {
          logger.error("updateEmail", error);
          throw new Error(error.message ?? "Unable to update email.");
        }
        // users table email is synced in onAuthStateChange on USER_UPDATED event
        logger.debug("updateEmail — confirmation email sent");
      },
      async updatePassword(currentPassword, newPassword) {
        const auth = getClient().auth;
        // Re-authenticate to verify current password
        const { data: { user } } = await auth.getUser();
        if (!user?.email) throw new Error("Not authenticated.");

        const { error: signInError } = await auth.signInWithPassword({
          email: user.email,
          password: currentPassword,
        });
        if (signInError) {
          logger.error("updatePassword re-auth", signInError);
          throw new Error("Current password is incorrect.");
        }

        const { error } = await auth.updateUser({ password: newPassword });
        if (error) {
          logger.error("updatePassword", error);
          throw new Error(error.message ?? "Unable to update password.");
        }
        logger.debug("updatePassword success");
      },
      async retrieveCheckout(referralCode, annual = false) {
        let userReferralCode = null;
        if (referralCode) userReferralCode = referralCode;
        const auth = getClient().auth;
        const { data, error } = await auth.getSession();
        if (data.session) {
          const token = data.session.access_token;
          const graphQLClient = new GraphQLClient(apiURL, {
            credentials: `include`,
            mode: `cors`,
            headers: {
              authorization: `Bearer ${token}`,
            },
          });
          return graphQLClient
            .request(Q_USERS_CHECKOUT, {
              input: {
                annual: annual,
                referralCode: userReferralCode,
                redirect: `${window.location.origin}/subscribed`,
                redirectCancel: `${window.location.origin}/start`,
              },
            })
            .then((result) => {
              logger.debug("retrieveCheckout => result", result);
              if (result.errors || result.usersCheckout.errors) {
                throw new Error(
                  result?.usersCheckout?.errors?.[0]?.message ??
                    "Unable to load your checkout session."
                );
              }
              return result.usersCheckout?.checkoutUrl;
            });
        } else {
          return Promise.resolve(null);
        }
      },
      async retrieveCheckoutLink(email, coupon = null, promotion = null) {
        const graphQLClient = new GraphQLClient(apiURL, {
          credentials: `include`,
          mode: `cors`,
        });
        return graphQLClient
          .request(Q_USER_CHECKOUT_LINK, { email, coupon, promotion })
          .then((result) => {
            logger.debug("retrieveCheckout => result", result);
            if (result.errors || result.userCheckoutLink.errors) {
              throw new Error(
                result?.userCheckoutLink?.errors?.[0]?.message ??
                  "Unable to load your checkout session."
              );
            }
            return result.userCheckoutLink?.checkoutUrl;
          });
      },
      async submitAuditAccess() {
        const self = this;
        const client = getClient();
        const auth = client.auth;
        const fingerprint = this.fingerprint;

        async function getFingerprint() {
          try {
            if (fingerprint) {
              return Promise.resolve({ visitorId: fingerprint });
            }
            const fpInstance = await FingerprintJS.load({ monitoring: false });
            return fpInstance.get();
          } catch (error) {
            logger.error("Error getting fingerprint:", error);
            return null;
          }
        }

        async function getIPAddress(retryCount = 3) {
          let attempts = 0;

          while (attempts < retryCount) {
            try {
              const response = await fetch("https://api.ipify.org?format=json");

              // Ensure the response is OK before proceeding
              if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
              }

              return await response.json();
            } catch (error) {
              attempts++;
              logger.error(
                `Attempt ${attempts} - Error getting IP address:`,
                error
              );

              // If this was the last attempt, return null
              if (attempts === retryCount) {
                return null;
              }
            }
          }
        }

        async function getSession() {
          try {
            const client = getClient();
            const auth = client.auth;
            return auth.getSession();
          } catch (error) {
            logger.error("Error getting session:", error);
            return null;
          }
        }

        async function logAuditAccess(fingerprint, ip, session) {
          try {
            const client = getClient();
            const payload = {
              id: crypto.randomUUID(),
              created: new Date().toISOString(),
              user_id: session.user.id,
              fingerprint,
              ip_address: ip,
            };

            const result = await client
              .from("access_audit")
              .insert(payload)
              .select("*");

            logger.debug("submitAuditAccess => result", result);
          } catch (error) {
            logger.error("Error logging audit access:", error);
          }
        }

        try {
          const [fingerprintResult, ipResult, sessionResult] =
            await Promise.all([getFingerprint(), getIPAddress(), getSession()]);
          this.fingerprint = fingerprintResult?.visitorId;
          if (
            sessionResult?.data?.session?.user &&
            ipResult?.ip &&
            sessionResult?.data?.session?.access_token
          ) {
            await logAuditAccess(
              fingerprintResult?.visitorId,
              ipResult?.ip,
              sessionResult.data.session
            );
          }
        } catch (error) {
          logger.error("Error in submitAuditAccess:", error);
        }
      },

      async requestAffiliateData() {
        const auth = getClient().auth;
        const { data, error } = await auth.getSession();
        const token = data.session.access_token;

        const graphQLClient = new GraphQLClient(apiURL, {
          credentials: `include`,
          mode: `cors`,
          headers: {
            authorization: `Bearer ${token}`,
          },
        });
        return graphQLClient.request(Q_USER_AFFILIATE, {}).then((result) => {
          logger.debug("requestAffiliateData => result", result);
          if (result.errors || result.userAffiliate.errors) {
            throw new Error(
              result?.userAffiliate?.errors?.[0]?.message ??
                "Unable to load your affiliate data."
            );
          }
          return result?.userAffiliate;
        });
      },

      async fetchWatchlists() {
        const client = getClient();
        try {
          const result = await client
            .from("watchlists")
            .select("*,watchlist_items(*)")
            .eq("user_id", this.profileId);
          logger.debug("fetchWatchlists => result", result);
          return result?.data;
        } catch (err) {
          logger.error("Error fetching watchlists...", err);
          throw err;
        }
      },

      async createWatchlist(name) {
        const client = getClient();
        try {
          const result = await client
            .from("watchlists")
            .insert({ name: name, user_id: this.profileId })
            .select();
          logger.debug("createWatchlist => result", result);
          return result?.data?.[0];
        } catch (err) {
          logger.error("Error creating watchlist...", err);
          throw err;
        }
      },

      async addWatchlistItem(id, item) {
        const client = getClient();
        try {
          const result = await client
            .from("watchlist_items")
            .insert({ watchlist_id: id, stock_symbol: item })
            .select()
            .single();
          logger.debug("addWatchlistItem => result", result);

          // Validate that the operation was successful and we have valid data
          if (result?.error) {
            logger.error("Database error in addWatchlistItem", result.error);
            throw new Error(`Database error: ${result.error.message}`);
          }

          if (!result?.data || !result.data.id) {
            logger.error(
              "Invalid or missing data in addWatchlistItem result",
              result
            );
            throw new Error(
              "Failed to create watchlist item - no valid data returned"
            );
          }

          return result.data;
        } catch (err) {
          logger.error("Error adding watchlist item...", err);
          throw err;
        }
      },

      async removeWatchlistItem(id) {
        const client = getClient();
        try {
          const result = await client
            .from("watchlist_items")
            .delete()
            .eq("id", id)
            .select()
            .single();
          logger.debug("removeWatchlistItem => result", result);

          // Validate that the operation was successful
          if (result?.error) {
            logger.error("Database error in removeWatchlistItem", result.error);
            throw new Error(`Database error: ${result.error.message}`);
          }

          // For delete operations, we expect either data (if .select() returns the deleted row)
          // or no error if the deletion was successful
          return result?.data;
        } catch (err) {
          logger.error("Error removing watchlist item...", err);
          throw err;
        }
      },

      async renameWatchlist(id, name) {
        const client = getClient();
        try {
          const result = await client
            .from("watchlists")
            .update({ name: name })
            .eq("id", id)
            .select()
            .single();
          logger.debug("renameWatchlist => result", result);

          if (result?.error) {
            logger.error("Database error in renameWatchlist", result.error);
            throw new Error(`Database error: ${result.error.message}`);
          }

          return result?.data;
        } catch (err) {
          logger.error("Error renaming watchlist...", err);
          throw err;
        }
      },

      async deleteWatchlist(id) {
        const client = getClient();
        try {
          // First delete all watchlist items (cascade)
          await client.from("watchlist_items").delete().eq("watchlist_id", id);

          // Then delete the watchlist itself
          const result = await client
            .from("watchlists")
            .delete()
            .eq("id", id)
            .select()
            .single();
          logger.debug("deleteWatchlist => result", result);

          if (result?.error) {
            logger.error("Database error in deleteWatchlist", result.error);
            throw new Error(`Database error: ${result.error.message}`);
          }

          return result?.data;
        } catch (err) {
          logger.error("Error deleting watchlist...", err);
          throw err;
        }
      },
    },
  },
  {
    persistedState: {
      // store options goes here
      excludePaths: [],
    },
  }
);
